import { AutocompleteWithApply } from '@components/AutocompleteWithApply';
import { ILabsFilter, laboratoriesApiSlice, setLabsIsChange, setProvidersScope } from '@src/api';
import { PAGE_SIZE } from '@src/api/common';
import { IntersectionType } from '@src/api/types';
import { useDebounce } from '@src/hooks/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { RootState } from '@src/store';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

const initialLabsFilter = {
  search: '',
  skip: 0,
  take: PAGE_SIZE,
};

export const LabsSelect = ({ disabled = false }) => {
  const { watch, setValue } = useFormContext();
  const { selectionId } = useParams();
  const scope = useSelector((state: RootState) => state.selectionScope);

  const dispatch = useDispatch();

  const [labsFilter, setLabsFilter] = useState<Nullable<ILabsFilter>>(initialLabsFilter);

  const [intersectionType, laboratoryIds, addedArr, removedArr] = watch([
    'createVisibility.laboratoryIntersectionType',
    'createVisibility.laboratoryIds',
    'createVisibility.addedLaboratoryIds',
    'createVisibility.removedLaboratoryIds',
  ]);
  const [value, setVal] = useState({ intersectionType, ids: laboratoryIds });
  const [initialIntersectionType, setInitialIntersectionType] = useState<Nullable<IntersectionType>>();

  const debouncedLabsFilter = useDebounce(labsFilter);

  useEffect(() => {
    if (scope?.isAllLaboratories) {
      setVal({ intersectionType: IntersectionType.Exclude, ids: [] });
      setInitialIntersectionType(IntersectionType.Exclude);
    } else setInitialIntersectionType(IntersectionType.Include);
  }, [scope?.isAllLaboratories]);

  useEffect(() => {
    if (intersectionType) setVal({ intersectionType, ids: laboratoryIds });
  }, [intersectionType, laboratoryIds]);

  const { labs, isFetching, isEnd } = laboratoriesApiSlice.useGetLabsQuery(
    {
      ...debouncedLabsFilter,
      selectionId: selectionId && !scope?.isAllLaboratories && !scope.visibilityIsChanged ? +selectionId : null,
    } as ILabsFilter,
    {
      skip: !debouncedLabsFilter,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          isEnd: data?.isEnd,
          labs: [
            ...(data?.data.map(({ id, name, isSelected }) => ({
              name,
              value: id,
              isSelected: isSelected || scope?.isAllLaboratories,
            })) || []),
          ],
        };
      },
    },
  );

  const onApply = (ids, addedIds, removedIds) => {
    const intersecionTypeIsNone =
      ((addedIds && addedIds?.length > 0) || (removedIds && removedIds.length > 0) || ids.length === 0) &&
      !scope?.isAllLaboratories;
    const intersectionTypeLab = intersecionTypeIsNone
      ? IntersectionType.None
      : scope?.isAllLaboratories
      ? IntersectionType.Exclude
      : IntersectionType.Include;

    setValue('createVisibility.laboratoryIntersectionType', intersectionTypeLab);
    setValue('createVisibility.laboratoryIds', intersecionTypeIsNone ? [] : ids);
    setValue('createVisibility.addedLaboratoryIds', addedIds);
    setValue('createVisibility.removedLaboratoryIds', removedIds);

    setValue('createVisibility.facilityIntersectionType', IntersectionType.Exclude);
    setValue('createVisibility.facilityVisibilities', []);
    dispatch(setLabsIsChange());
    dispatch(setProvidersScope(true));
  };

  const onSearch = useCallback((val: string) => {
    if (val?.length > 1 || val?.length < 1)
      setLabsFilter(prev => (prev ? { ...prev, skip: 0, search: val || '' } : null));
  }, []);

  return (
    <AutocompleteWithApply
      noOptionsText="You don't have any Laboratories yet."
      options={
        labs && labs.length > 0
          ? [{ name: 'All laboratories', value: 'all', isSelected: scope?.isAllLaboratories }, ...labs]
          : []
      }
      value={value}
      placeholder="Laboratories"
      inputPlaceholder="laboratories"
      onSearch={onSearch}
      onApply={onApply}
      loading={isFetching}
      isEnd={isEnd}
      setValuesFilter={setLabsFilter}
      addedArr={addedArr}
      removedArr={removedArr}
      initialIntersectionType={initialIntersectionType}
      intersectionTypeFormValue={intersectionType}
      searchValue={labsFilter?.search}
      disabled={disabled}
      scopeName="isAllLaboratories"
    />
  );
};
